import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Box, Typography, Card, CardContent, CircularProgress, Button, TextField, Tooltip } from '@mui/material';
import {
  CheckCircle as StatusIcon,
  Warning as AlertIcon,
  PlayArrow as RunIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  PlayArrow as ResumeIcon,
} from '@mui/icons-material';
import type { Experiment } from '@/types/experiment';
import { useExperimentStore } from '@/store/experimentStore';
import { useRaspiStore } from '@/store/raspiStore';
import { useChamberStore } from '@/store/chamberStore';
import { useCameraStore } from '@/store/cameraStore';
import { useAuthStore } from '@/store/authStore';
import { useUnauthorizedAction } from '../../App';
import { useNotifier } from '../NotificationProvider';
import { sendExperimentCommand } from '@/services/api';
import ExperimentRunConfirmationDialog from '../dialogs/ExperimentRunConfirmationDialog';
import PauseExperimentDialog from '../dialogs/PauseExperimentDialog';
import StopExperimentDialog from '../dialogs/StopExperimentDialog';
import type { Chamber, Camera, RaspiDevice } from '@/types/raspi';
import { useNavigate } from 'react-router-dom';
import { formatUtcToLocalDate, formatUtcToLocalTime } from '@/utils/timeUtils';
import { useTheme } from '@mui/material/styles';

interface ExperimentCardProps {
  selectedRaspiId: string | null;
}

const ExperimentCard: React.FC<ExperimentCardProps> = ({ selectedRaspiId }) => {
  const theme = useTheme();
  const {
    allExperiments,
    isLoading,
    fetchAllExperiments,
    updateExperimentFromUser
  } = useExperimentStore();
  
  const { fetchAllRaspis } = useRaspiStore();
  const { fetchAllChambers } = useChamberStore();
  const { fetchAllCameras } = useCameraStore();
  
  // Create stable references for fetch functions to prevent infinite re-renders
  const fetchAllExperimentsRef = useRef(fetchAllExperiments);
  
  const { getRole } = useAuthStore();
  const isViewer = getRole() === 'viewer' || getRole() === 'public';
  const triggerUnauthorized = useUnauthorizedAction();
  const navigate = useNavigate();
  const { runWithNotification, notify } = useNotifier();

  // State for run confirmation dialog
  const [showRunConfirmation, setShowRunConfirmation] = useState(false);
  const [isStartingExperiment, setIsStartingExperiment] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // State for pause dialog
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [isPausingExperiment, setIsPausingExperiment] = useState(false);
  // State for stop dialog
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [isStoppingExperiment, setIsStoppingExperiment] = useState(false);
  // Resume in flight
  const [isResumingExperiment, setIsResumingExperiment] = useState(false);

  // Any experiment-control action in flight — used to disable run/stop/pause/resume buttons
  const isExperimentActionInFlight =
    isStartingExperiment || isStoppingExperiment || isPausingExperiment || isResumingExperiment;

  // Alert description visibility state
  const [showAlertDescription, setShowAlertDescription] = useState<boolean>(false);

  // ✅ Store-first approach: Only fetch if store is empty (like ChamberCard/CameraCard)
  useEffect(() => {
    const hasData = Object.keys(allExperiments).length > 0;
    if (!hasData) {
      fetchAllExperimentsRef.current();
    }
  }, [allExperiments]);

  // ✅ Memoize selected experiment to avoid recalculating on every render
  const experiment = useMemo((): Experiment | null => {
    if (!selectedRaspiId) return null;
    
    // Find all experiments for this RASPI from store
    const raspiExperiments = Object.values(allExperiments).filter(
      exp => exp.raspi_id === selectedRaspiId
    );
    
    if (raspiExperiments.length === 0) return null;
    
    // Sort by exp_id to get the latest (highest number)
    const sortedExperiments = [...raspiExperiments].sort((a, b) => {
      const aNum = parseInt(a.exp_id.replace('E', '')) || 0;
      const bNum = parseInt(b.exp_id.replace('E', '')) || 0;
      return bNum - aNum; // Descending order (latest first)
    });
    
    return sortedExperiments[0] || null;
  }, [selectedRaspiId, allExperiments]);

  // Keep getSelectedExperiment for backward compatibility with existing code
  // Note: experiment is now memoized, so this just returns the memoized value
  const getSelectedExperiment = (): Experiment | null => experiment;

  // Get current experiment ID being displayed
  const getCurrentExperimentId = (): string => {
    const experiment = getSelectedExperiment();
    return experiment?.exp_id || 'None';
  };

  // Get current values - no defaults, only actual database values
  const getCurrentTestId = (): string => {
    const experiment = getSelectedExperiment();
    return experiment?.test_id || '';
  };

  const getCurrentProfileId = (): string => {
    const experiment = getSelectedExperiment();
    return experiment?.profile_id || '';
  };

  const getCurrentNumProfiles = (): number => {
    const experiment = getSelectedExperiment();
    return experiment?.num_profiles || 0;
  };

  // Helper to safely get ISO string from date or string
  const getIsoString = (dateVal: any): string => {
    if (!dateVal) return '';
    if (typeof dateVal === 'string') {
      // If already ISO string, return as is, else parse and return ISO
      const d = new Date(dateVal);
      return isNaN(d.getTime()) ? '' : d.toISOString();
    }
    if (dateVal instanceof Date) return dateVal.toISOString();
    return '';
  };

  const getCurrentStartTime = (): string => {
    const experiment = getSelectedExperiment();
    return getIsoString(experiment?.start_time);
  };

  const getCurrentEndTime = (): string => {
    const experiment = getSelectedExperiment();
    return getIsoString(experiment?.end_time);
  };

  // Helper function to format time display - split date and time (UTC to local)
  const formatTimeDisplay = (timeString: string): { date: string; time: string } => {
    if (!timeString || timeString.trim() === '') {
      return { date: '', time: '' };
    }
    
    try {
      const date = new Date(timeString);
      if (isNaN(date.getTime())) {
        return { date: '', time: '' };
      }
      
      return {
        date: formatUtcToLocalDate(date),
        time: formatUtcToLocalTime(date)
      };
    } catch (error) {
      return { date: '', time: '' };
    }
  };

  // Helper functions for status and alert icons/colors
  const getStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'NEW':
        return <StatusIcon sx={{ color: '#2196f3', fontSize: theme.iconSizes.small }} />;
      case 'RUN':
        return <StatusIcon sx={{ color: '#4caf50', fontSize: theme.iconSizes.small }} />;
      case 'PAUSE':
        return <StatusIcon sx={{ color: '#ff9800', fontSize: theme.iconSizes.small }} />;
      case 'STOP':
        return <StatusIcon sx={{ color: '#9e9e9e', fontSize: theme.iconSizes.small }} />;
      case 'RESUME':
        return <StatusIcon sx={{ color: '#4caf50', fontSize: theme.iconSizes.small }} />;
      case 'ASK':
        return <StatusIcon sx={{ color: '#ff9800', fontSize: theme.iconSizes.small }} />;
      default:
        return <StatusIcon sx={{ color: '#9e9e9e', fontSize: theme.iconSizes.small }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'NEW':
        return '#2196f3'; // Blue - newly created experiment
      case 'RUN':
        return '#4caf50'; // Green - experiment is running
      case 'PAUSE':
        return '#ff9800'; // Orange - experiment is paused
      case 'STOP':
        return '#9e9e9e'; // Gray - experiment is stopped
      case 'RESUME':
        return '#4caf50'; // Green - experiment is resuming
      case 'ASK':
        return '#ff9800'; // Orange - experiment is asking
      default:
        return '#9e9e9e'; // Gray - unknown status
    }
  };

  const getAlertIcon = (alertLevel: string) => {
    switch (alertLevel?.toUpperCase()) {
      case 'HIGH':
        return <AlertIcon sx={{ color: '#f44336', fontSize: theme.iconSizes.small }} />;
      case 'LOW':
        return <AlertIcon sx={{ color: '#ff9800', fontSize: theme.iconSizes.small }} />;
      case 'NO':
        return <AlertIcon sx={{ color: '#4caf50', fontSize: theme.iconSizes.small }} />;
      default:
        return <AlertIcon sx={{ color: '#9e9e9e', fontSize: theme.iconSizes.small }} />;
    }
  };

  const getAlertColor = (alertLevel: string) => {
    switch (alertLevel?.toUpperCase()) {
      case 'HIGH':
        return '#f44336'; // Red - critical alert
      case 'LOW':
        return '#ff9800'; // Orange - warning alert
      case 'NO':
        return '#4caf50'; // Green - no alerts
      default:
        return '#9e9e9e'; // Gray - unknown alert level
    }
  };

  // Complete system validation for run confirmation - now takes store data as parameters
  // Note: experiment is already memoized above, no need to redeclare
  const validateExperimentForRun = (
    experiment: Experiment,
    allChambers: Record<string, Chamber>,
    allCameras: Record<string, Camera>,
    allRaspis: Record<string, RaspiDevice>
  ): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    // Check required experiment fields
    if (!experiment.test_id?.trim()) {
      errors.push('Test ID is required');
    }
    
    if (!experiment.profile_id?.trim()) {
      errors.push('Profile ID is required');
    }
    
    if (experiment.num_profiles <= 0) {
      errors.push('Number of profiles must be greater than 0');
    }
    
    // Check experiment status - prevent running already running experiments
    if (experiment.exp_status === 'RUN') {
      errors.push('Experiment is already running');
    }
    
    // Validate RASPI connection first (critical check)
    if (experiment.raspi_id) {
      const raspi = allRaspis[experiment.raspi_id];
      if (!raspi) {
        errors.push(`RASPI ${experiment.raspi_id} not found in system`);
      } else if (raspi.raspi_status === 'DISCONNECTED') {
        errors.push(`RASPI ${experiment.raspi_id} is disconnected`);
      }
    } else {
      errors.push('Experiment has no assigned RASPI');
    }
    
    // Check peripheral availability
    const hasChamber = experiment.chamber_enabled;
    const hasCameras = experiment.cameras_enabled?.some(c => c.enabled);
    if (!hasChamber && !hasCameras) {
      errors.push('At least one peripheral (chamber or camera) must be enabled');
    }
    
    // Validate chamber if enabled
    if (hasChamber && experiment.chamber_id) {
      const chamber = allChambers[experiment.chamber_id];
      if (!chamber) {
        errors.push(`Chamber ${experiment.chamber_id} not found in system`);
      } else if (chamber.chamber_status === 'DISCONNECTED') {
        errors.push(`Chamber ${experiment.chamber_id} is disconnected`);
      }
    }
    
    // Validate cameras if enabled
    if (experiment.cameras_enabled) {
      const enabledCameras = experiment.cameras_enabled.filter(c => c.enabled);
      for (const camera of enabledCameras) {
        const cameraData = allCameras[camera.camera_id];
        if (!cameraData) {
          errors.push(`Camera ${camera.camera_id} not found in system`);
          continue;
        }
        
        // Check camera status
        if (cameraData.camera_status === 'DISCONNECTED') {
          errors.push(`Camera ${camera.camera_id} is disconnected`);
        }
        
        // Check gauge configuration
        if (!cameraData.camera_gauge_id?.trim()) {
          errors.push(`Camera ${camera.camera_id} requires a Gauge ID to be configured`);
        }
        
        // Check LED configuration
        // if (!cameraData.camera_led_id?.trim()) {
        //   errors.push(`Camera ${camera.camera_id} requires an LED ID to be configured`);
        // }
      }
    }
    
    return { isValid: errors.length === 0, errors };
  };

  // Control button handlers
  const handleRunExperiment = async () => {
    if (!experiment?.exp_id) return;
    
    // Don't validate here - just show confirmation dialog
    // Validation will happen at execution time in handleConfirmRun
    setValidationErrors([]);
    setShowRunConfirmation(true);
  };

  const handleConfirmRun = async () => {
    const experiment = getSelectedExperiment();
    if (!experiment) return;
    if (isExperimentActionInFlight) return;

    // Validate at execution time with fresh store data
    const allChambers = useChamberStore.getState().allChambers;
    const allCameras = useCameraStore.getState().allCameras;
    const allRaspis = useRaspiStore.getState().allRaspis;

    const validation = validateExperimentForRun(experiment, allChambers, allCameras, allRaspis);

    if (!validation.isValid) {
      // Show validation errors in dialog AND surface a brief toaster
      setValidationErrors(validation.errors);
      notify(`Cannot start experiment: ${validation.errors[0]}`, 'error');
      return;
    }

    // Clear any validation errors since validation passed
    setValidationErrors([]);
    setIsStartingExperiment(true);

    try {
      const result = await runWithNotification(
        async () => {
          const commandSuccess = await sendExperimentCommand(experiment.exp_id, 'RUN');
          if (!commandSuccess) throw new Error('Hub rejected RUN command');
          await Promise.all([
            fetchAllExperiments(),
            fetchAllRaspis(),
            fetchAllChambers(),
            fetchAllCameras(),
          ]);
        },
        {
          pending: `Starting experiment ${experiment.exp_id}...`,
          success: `Experiment ${experiment.exp_id} started`,
          error: (err) => {
            // Extract a friendly message from axios-style errors
            const e = err as any;
            const detail = e?.response?.data?.detail;
            if (typeof detail === 'string') {
              if (detail.includes('disconnected')) return 'RASPI is disconnected. Check the device connection.';
              if (detail.includes('not found')) return 'Device not found. Refresh the system.';
              return detail;
            }
            return 'Failed to start experiment. Please try again.';
          },
        }
      );

      if (result !== null) {
        setShowRunConfirmation(false);
      }
    } finally {
      setIsStartingExperiment(false);
    }
  };

  const handleCancelRun = () => {
    setShowRunConfirmation(false);
    setValidationErrors([]);
  };

  const handleStopExperiment = async () => {
    if (!experiment?.exp_id) return;

    // Check current status before proceeding
    if (experiment.exp_status === 'STOP' || experiment.exp_status === 'NEW') {
      notify(`No experiment is currently running on ${selectedRaspiId}`, 'info');
      return;
    }

    // Show confirmation dialog for active experiments
    setShowStopDialog(true);
  };

  const handleConfirmStop = async () => {
    if (!experiment?.exp_id || isExperimentActionInFlight) return;

    setIsStoppingExperiment(true);
    try {
      const result = await runWithNotification(
        async () => {
          const success = await sendExperimentCommand(experiment.exp_id, 'STOP');
          if (!success) throw new Error('Hub rejected STOP command');
          await Promise.all([
            fetchAllExperiments(),
            fetchAllRaspis(),
            fetchAllChambers(),
            fetchAllCameras(),
          ]);
        },
        {
          pending: `Stopping experiment ${experiment.exp_id}...`,
          success: `Experiment ${experiment.exp_id} stopped`,
          error: 'Failed to stop experiment',
        }
      );
      if (result !== null) {
        setShowStopDialog(false);
      }
    } finally {
      setIsStoppingExperiment(false);
    }
  };

  const handleCancelStop = () => {
    if (isStoppingExperiment) return;
    setShowStopDialog(false);
  };

  const handlePauseExperiment = async () => {
    if (!experiment?.exp_id) return;

    // Check if experiment is running
    if (experiment.exp_status !== 'RUN') {
      notify(`Cannot pause: experiment status is ${experiment.exp_status}`, 'info');
      return;
    }

    setShowPauseDialog(true);
  };

  const handleConfirmPause = async (temperature: number) => {
    if (!experiment?.exp_id || isExperimentActionInFlight) return;

    setIsPausingExperiment(true);
    try {
      const result = await runWithNotification(
        async () => {
          const success = await sendExperimentCommand(experiment.exp_id, 'PAUSE', { temperature_request: temperature });
          if (!success) throw new Error('Hub rejected PAUSE command');
          await Promise.all([
            fetchAllExperiments(),
            fetchAllRaspis(),
            fetchAllChambers(),
            fetchAllCameras(),
          ]);
        },
        {
          pending: `Pausing experiment ${experiment.exp_id}...`,
          success: `Experiment ${experiment.exp_id} paused`,
          error: 'Failed to pause experiment',
        }
      );
      if (result !== null) {
        setShowPauseDialog(false);
      }
    } finally {
      setIsPausingExperiment(false);
    }
  };

  const handleCancelPause = () => {
    if (isPausingExperiment) return;
    setShowPauseDialog(false);
  };

  // Get current chamber temperature - for now return default
  const getCurrentChamberTemperature = (): number => {
    // TODO: Implement getting actual temperature from chamber measurements
    // For now, return a reasonable default temperature
    return 25; // Default to 25°C
  };

  const handleResumeExperiment = async () => {
    if (!experiment?.exp_id || isExperimentActionInFlight) return;

    setIsResumingExperiment(true);
    try {
      await runWithNotification(
        async () => {
          // Update local status optimistically
          await updateExperimentFromUser(experiment.exp_id, { exp_status: 'RUN' });
          const success = await sendExperimentCommand(experiment.exp_id, 'RESUME');
          if (!success) throw new Error('Hub rejected RESUME command');
          await Promise.all([
            fetchAllExperiments(),
            fetchAllRaspis(),
            fetchAllChambers(),
            fetchAllCameras(),
          ]);
        },
        {
          pending: `Resuming experiment ${experiment.exp_id}...`,
          success: `Experiment ${experiment.exp_id} resumed`,
          error: 'Failed to resume experiment',
        }
      );
    } finally {
      setIsResumingExperiment(false);
    }
  };

  // Handler for navigating to Experiments page with selected exp_id
  const handleGoToExperimentsPage = (expId: string) => {
    navigate(`/experiments?exp_id=${expId}`);
  };

  const handleAlertIconClick = () => {
    setShowAlertDescription(!showAlertDescription);
  };

  // Show loading state when store is fetching data
  if (isLoading) {
    return (
      <Card sx={{ backgroundColor: 'white' }}>
        <CardContent sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              Loading experiment data...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ backgroundColor: 'white' }}>
        <CardContent sx={{ p: 2 }}>
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 1fr',
          gridTemplateRows: 'auto',
          gap: 1,
          gridTemplateAreas: `
            "exp_id exp_details exp_controls exp_monitor"
          `
        }}>
          {/* Experiment ID Box */}
          <Box sx={{ 
            gridArea: 'exp_id',
            border: '1px solid #ddd', 
            borderRadius: 1, 
            p: 1.5,
            backgroundColor: 'white',
            position: 'relative',
            '&::before': {
              content: '"EXPERIMENT ID"',
              position: 'absolute',
              top: '-8px',
              left: '8px',
              backgroundColor: 'white',
              padding: '0 4px',
              fontSize: theme.typography.sectionHeader.fontSize,
              fontWeight: 'bold',
              color: 'text.secondary',
              zIndex: 1
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
              {/* Experiment ID without border */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', flex: 2 }}>
                  <Typography
                  variant="entityId"
                  sx={{ color: 'text.primary', textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => handleGoToExperimentsPage(getCurrentExperimentId())}
                >
                  {getCurrentExperimentId()}
                </Typography>
              </Box>
              
              {/* Status and Alert icons in one line */}
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flex: 1 }}>
                {/* Status */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: 0,
                  p: 0.5,
                  borderRadius: 1,
                  backgroundColor: experiment ? 
                    (experiment?.exp_status === 'STOP' ? '#ffebee' : 
                     experiment?.exp_status === 'PAUSE' ? '#fff3e0' : 'transparent') : 'transparent',
                  border: experiment ? 
                    (experiment?.exp_status === 'STOP' ? '1px solid #ffcdd2' : 
                     experiment?.exp_status === 'PAUSE' ? '1px solid #ffe0b2' : 'none') : 'none'
                }}>
                  {experiment ? getStatusIcon(experiment?.exp_status || 'UNKNOWN') : <StatusIcon sx={{ color: '#9e9e9e', fontSize: theme.iconSizes.small }} />}
                  <Typography variant="iconValueText" sx={{ textAlign: 'center', color: experiment ? getStatusColor(experiment?.exp_status || 'UNKNOWN') : '#9e9e9e' }}>
                    {experiment ? (experiment?.exp_status || 'UNKNOWN') : 'N/A'}
                  </Typography>
                  <Typography variant="iconLabelText" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                    STATUS
                  </Typography>
                </Box>
                
                {/* Alert */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: 0,
                  p: 0.5,
                  borderRadius: 1,
                  backgroundColor: experiment ? 
                    (experiment?.exp_alert === 'HIGH' ? '#ffebee' : 
                     experiment?.exp_alert === 'LOW' ? '#fff3e0' : 'transparent') : 'transparent',
                  border: experiment ? 
                    (experiment?.exp_alert === 'HIGH' ? '1px solid #ffcdd2' : 
                     experiment?.exp_alert === 'LOW' ? '1px solid #ffe0b2' : 'none') : 'none'
                }}>
                  <Tooltip title={experiment?.exp_alert_description || ''} arrow>
                    <span 
                      onClick={handleAlertIconClick}
                      style={{ 
                        cursor: 'pointer',
                        transition: 'opacity 0.2s ease',
                        opacity: 1
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '0.7';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                    >
                      {experiment ? getAlertIcon(experiment?.exp_alert || 'NO') : <AlertIcon sx={{ color: '#9e9e9e', fontSize: theme.iconSizes.small }} />}
                    </span>
                  </Tooltip>
                  <Typography variant="iconValueText" sx={{ textAlign: 'center', color: experiment ? getAlertColor(experiment?.exp_alert || 'NO') : '#9e9e9e' }}>
                    {experiment ? (experiment?.exp_alert || 'NO') : 'N/A'}
                  </Typography>
                  <Typography variant="iconLabelText" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                    ALERT
                  </Typography>
                </Box>
              </Box>
              
              {/* Alert Description - shown when alert icon is clicked */}
              {showAlertDescription && experiment?.exp_alert_description && (
                <Box sx={{ 
                  mt: 1, 
                  p: 1, 
                  backgroundColor: '#fff3e0', 
                  border: '1px solid #ffe0b2',
                  borderRadius: 1,
                  borderLeft: '4px solid #ff9800'
                }}>
                  <Typography 
                    variant="alertLabel"
                    sx={{ 
                      color: '#e65100',
                      fontWeight: 'medium',
                      display: 'block'
                    }}
                  >
                    Alert: {experiment?.exp_alert_description}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          
          {/* Experiment Details Box */}
          <Box sx={{ 
            gridArea: 'exp_details',
            border: '1px solid #ddd', 
            borderRadius: 1, 
            p: 1.5,
            backgroundColor: 'white',
            position: 'relative',
            '&::before': {
              content: '"DETAILS"',
              position: 'absolute',
              top: '-8px',
              left: '8px',
              backgroundColor: 'white',
              padding: '0 4px',
              fontSize: theme.typography.sectionHeader.fontSize,
              fontWeight: 'bold',
              color: 'text.secondary',
              zIndex: 1
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Test ID, Profile ID, and Num Profiles in same row */}
              <Box sx={{ display: 'flex', gap: 1, position: 'relative' }}>
                {/* Test ID */}
                <Box sx={theme.fieldLabelContainer}>
                  <TextField
                    value={getCurrentTestId()}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      readOnly: true,
                      sx: {
                        '& .MuiInputBase-input': {
                          textAlign: 'left',
                          fontSize: theme.typography.fieldText.fontSize,
                          fontWeight: 'bold',
                          padding: '8px 8px'
                        }
                      }
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#ddd',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bbb',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                  <Typography 
                    variant="fieldLabel"
                    sx={theme.fieldLabelPositioned}
                  >
                    TEST ID
                  </Typography>
                </Box>
                
                {/* Profile ID */}
                <Box sx={theme.fieldLabelContainer}>
                  <TextField
                    value={getCurrentProfileId()}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      readOnly: true,
                      sx: {
                        '& .MuiInputBase-input': {
                          textAlign: 'left',
                          fontSize: theme.typography.fieldText.fontSize,
                          fontWeight: 'bold',
                          padding: '8px 8px'
                        }
                      }
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#ddd',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bbb',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                  <Typography 
                    variant="fieldLabel"
                    sx={theme.fieldLabelPositioned}
                  >
                    PROFILE ID
                  </Typography>
                </Box>
                
                {/* Num Profiles */}
                <Box sx={theme.fieldLabelContainer}>
                  <TextField
                    value={getCurrentNumProfiles().toString()}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      readOnly: true,
                      sx: {
                        '& .MuiInputBase-input': {
                          textAlign: 'left',
                          fontSize: theme.typography.fieldText.fontSize,
                          fontWeight: 'bold',
                          padding: '8px 8px'
                        }
                      }
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#ddd',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bbb',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                  <Typography 
                    variant="fieldLabel"
                    sx={theme.fieldLabelPositioned}
                  >
                    NUM PROFILES
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          
          {/* Experiment Controls Box */}
          <Box sx={{ 
            gridArea: 'exp_controls',
            border: '1px solid #ddd', 
            borderRadius: 1, 
            p: 1.5,
            backgroundColor: 'white',
            position: 'relative',
            '&::before': {
              content: '"CONTROLS"',
              position: 'absolute',
              top: '-8px',
              left: '8px',
              backgroundColor: 'white',
              padding: '0 4px',
              fontSize: theme.typography.sectionHeader.fontSize,
              fontWeight: 'bold',
              color: 'text.secondary',
              zIndex: 1
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Executive Buttons */}
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RunIcon />}
                  onClick={isViewer ? () => triggerUnauthorized() : handleRunExperiment}
                  disabled={!isViewer && isExperimentActionInFlight}
                  sx={{
                    typography: 'buttonLabel',
                    fontWeight: 'bold',
                    borderColor: '#4caf50',
                    color: '#4caf50',
                    '& .MuiButton-startIcon': {
                      marginRight: '2px',
                      display: 'flex',
                      alignItems: 'center'
                    },
                    '& .MuiButton-startIcon > *:nth-of-type(1)': {
                      fontSize: '1rem'
                    },
                    '&:hover': {
                      backgroundColor: '#4caf50',
                      color: 'white',
                      borderColor: '#4caf50'
                    }
                  }}
                >
                  RUN
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<StopIcon />}
                  onClick={isViewer ? () => triggerUnauthorized() : handleStopExperiment}
                  disabled={!isViewer && isExperimentActionInFlight}
                  sx={{
                    typography: 'buttonLabel',
                    fontWeight: 'bold',
                    borderColor: '#f44336',
                    color: '#f44336',
                    '& .MuiButton-startIcon': {
                      marginRight: '2px',
                      display: 'flex',
                      alignItems: 'center'
                    },
                    '& .MuiButton-startIcon > *:nth-of-type(1)': {
                      fontSize: '1rem'
                    },
                    '&:hover': {
                      backgroundColor: '#f44336',
                      color: 'white',
                      borderColor: '#f44336'
                    }
                  }}
                >
                  STOP
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PauseIcon />}
                  onClick={isViewer ? () => triggerUnauthorized() : handlePauseExperiment}
                  disabled={!isViewer && isExperimentActionInFlight}
                  sx={{
                    typography: 'buttonLabel',
                    fontWeight: 'bold',
                    borderColor: '#ff9800',
                    color: '#ff9800',
                    '& .MuiButton-startIcon': {
                      marginRight: '2px',
                      display: 'flex',
                      alignItems: 'center'
                    },
                    '& .MuiButton-startIcon > *:nth-of-type(1)': {
                      fontSize: '1rem'
                    },
                    '&:hover': {
                      backgroundColor: '#ff9800',
                      color: 'white',
                      borderColor: '#ff9800'
                    }
                  }}
                >
                  PAUSE
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ResumeIcon />}
                  onClick={isViewer ? () => triggerUnauthorized() : handleResumeExperiment}
                  disabled={!isViewer && isExperimentActionInFlight}
                  sx={{
                    typography: 'buttonLabel',
                    fontWeight: 'bold',
                    borderColor: '#2196f3',
                    color: '#2196f3',
                    '& .MuiButton-startIcon': {
                      marginRight: '2px',
                      display: 'flex',
                      alignItems: 'center'
                    },
                    '& .MuiButton-startIcon > *:nth-of-type(1)': {
                      fontSize: '1rem'
                    },
                    '&:hover': {
                      backgroundColor: '#2196f3',
                      color: 'white',
                      borderColor: '#2196f3'
                    }
                  }}
                >
                  RESUME
                </Button>
              </Box>
            </Box>
          </Box>
          
          {/* Experiment Monitor Box */}
          <Box sx={{ 
            gridArea: 'exp_monitor',
            border: '1px solid #ddd', 
            borderRadius: 1, 
            p: 1.5,
            backgroundColor: 'white',
            position: 'relative',
            '&::before': {
              content: '"MONITORING"',
              position: 'absolute',
              top: '-8px',
              left: '8px',
              backgroundColor: 'white',
              padding: '0 4px',
              fontSize: theme.typography.sectionHeader.fontSize,
              fontWeight: 'bold',
              color: 'text.secondary',
              zIndex: 1
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Start Time and End Time in same row */}
              <Box sx={{ display: 'flex', gap: 1, position: 'relative' }}>
                {/* Start Time */}
                <Box sx={theme.fieldLabelContainer}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                    {/* Start Date */}
                    <TextField
                      value={formatTimeDisplay(getCurrentStartTime()).date}
                      variant="outlined"
                      size="small"
                      InputProps={{
                        readOnly: true,
                        sx: {
                          '& .MuiInputBase-input': {
                            textAlign: 'left',
                            fontSize: theme.typography.fieldText.fontSize,
                            fontWeight: 'bold',
                            padding: '6px 8px'
                          }
                        }
                      }}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#ddd',
                          },
                          '&:hover fieldset': {
                            borderColor: '#bbb',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                    {/* Start Time */}
                    <TextField
                      value={formatTimeDisplay(getCurrentStartTime()).time}
                      variant="outlined"
                      size="small"
                      InputProps={{
                        readOnly: true,
                        sx: {
                          '& .MuiInputBase-input': {
                            textAlign: 'left',
                            fontSize: theme.typography.fieldText.fontSize,
                            fontWeight: 'bold',
                            padding: '6px 8px'
                          }
                        }
                      }}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#ddd',
                          },
                          '&:hover fieldset': {
                            borderColor: '#bbb',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Box>
                  <Typography 
                    variant="fieldLabel"
                    sx={theme.fieldLabelPositioned}
                  >
                    START TIME
                  </Typography>
                </Box>
                
                {/* End Time */}
                <Box sx={theme.fieldLabelContainer}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                    {/* End Date */}
                    <TextField
                      value={formatTimeDisplay(getCurrentEndTime()).date}
                      variant="outlined"
                      size="small"
                      InputProps={{
                        readOnly: true,
                        sx: {
                          '& .MuiInputBase-input': {
                            textAlign: 'left',
                            fontSize: theme.typography.fieldText.fontSize,
                            fontWeight: 'bold',
                            padding: '6px 8px'
                          }
                        }
                      }}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#ddd',
                          },
                          '&:hover fieldset': {
                            borderColor: '#bbb',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                    {/* End Time */}
                    <TextField
                      value={formatTimeDisplay(getCurrentEndTime()).time}
                      variant="outlined"
                      size="small"
                      InputProps={{
                        readOnly: true,
                        sx: {
                          '& .MuiInputBase-input': {
                            textAlign: 'left',
                            fontSize: theme.typography.fieldText.fontSize,
                            fontWeight: 'bold',
                            padding: '6px 8px'
                          }
                        }
                      }}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#ddd',
                          },
                          '&:hover fieldset': {
                            borderColor: '#bbb',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Box>
                  <Typography 
                    variant="fieldLabel"
                    sx={theme.fieldLabelPositioned}
                  >
                    END TIME
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
    
    {/* Confirmation Dialog */}
    <ExperimentRunConfirmationDialog
      open={showRunConfirmation}
      onClose={handleCancelRun}
      onConfirm={handleConfirmRun}
      experiment={experiment}
      isLoading={isStartingExperiment}
      validationErrors={validationErrors}
    />

    {/* Pause Dialog */}
    <PauseExperimentDialog
      open={showPauseDialog}
      onClose={handleCancelPause}
      onConfirm={handleConfirmPause}
      experiment={experiment}
      currentTemperature={getCurrentChamberTemperature()}
      isLoading={isPausingExperiment}
    />

    {/* Stop Dialog */}
    <StopExperimentDialog
      open={showStopDialog}
      onClose={handleCancelStop}
      onConfirm={handleConfirmStop}
      experiment={experiment}
      isStopping={isStoppingExperiment}
    />
  </>
  );
};

export default ExperimentCard; 